const LAST_UPDATED = "October 2026";

export const LEGAL_PAGES = {
  privacy: {
    path: "/privacy-policy",
    title: "Privacy Policy",
    updated: LAST_UPDATED,
    intro: "This Privacy Policy explains how Rupora collects, uses and protects your information when you use the Rupora platform and applications.",
    sections: [
      {
        h: "Information We Collect",
        body: "We collect the information needed to operate your account and process transactions: your name, email address, account credentials, transaction details, bank account information you provide for payouts, and basic device and usage data required to keep the platform secure.",
      },
      {
        h: "How We Use Information",
        body: "Your information is used to create and secure your account, verify and process transactions, maintain transaction records, provide support, and meet applicable verification and compliance requirements. We do not sell your personal information.",
      },
      {
        h: "Transaction Records",
        body: "Deposits, exchanges, withdrawals and related verification records are retained as part of your account history and for compliance, dispute-resolution and security purposes.",
      },
      {
        h: "Data Security",
        body: "Access to your account is protected by authenticated sessions, and sensitive data is transmitted over encrypted connections. Access to operational data is restricted to authorized personnel only.",
      },
      {
        h: "Sharing",
        body: "We share information only where required to process your transactions (for example, banking/payout rails), to comply with legal obligations, or to protect the platform against fraud and abuse.",
      },
      {
        h: "Your Choices",
        body: "You may review and update your account information inside the app. For access, correction or deletion requests, contact support through the Rupora app.",
      },
      {
        h: "Changes",
        body: "We may update this policy from time to time. Material changes will be reflected on this page with an updated revision date.",
      },
    ],
  },
  terms: {
    path: "/terms-and-conditions",
    title: "Terms & Conditions",
    updated: LAST_UPDATED,
    intro: "These Terms govern your use of the Rupora platform. By creating an account or using Rupora, you agree to these Terms.",
    sections: [
      {
        h: "The Service",
        body: "Rupora provides a clean fund exchange service with a verification-first process. Transactions are processed only after the required verification and compliance checks are completed.",
      },
      {
        h: "Accounts",
        body: "You are responsible for keeping your account credentials confidential and for all activity under your account. You must provide accurate information during registration and verification.",
      },
      {
        h: "Rates & Processing",
        body: "Rates shown on the website or in the app are indicative. The final rate for a transaction depends on market conditions, volume, fund type and transaction verification, and is confirmed before processing. Processing times can vary with network confirmations and banking hours.",
      },
      {
        h: "Guarantee Coverage",
        body: "Where applicable, guarantee coverage is subject to transaction eligibility, verification, applicable terms and approved transaction records. It does not apply to fraudulent, unauthorized, disputed or otherwise excluded transactions.",
      },
      {
        h: "Prohibited Use",
        body: "You may not use Rupora for unlawful activity, fraud, money laundering, or to transact funds you are not authorized to use. We may refuse, hold or reverse transactions that fail verification or violate these Terms.",
      },
      {
        h: "Limitation of Liability",
        body: "To the maximum extent permitted by law, Rupora is not liable for indirect or consequential losses, market movements, or delays caused by banking networks, blockchain networks or third parties.",
      },
      {
        h: "Changes & Contact",
        body: "We may update these Terms from time to time. Continued use of the platform after an update constitutes acceptance. Questions can be raised through the support section in the Rupora app.",
      },
    ],
  },
  risk: {
    path: "/risk-disclosure",
    title: "Risk Disclosure",
    updated: LAST_UPDATED,
    intro: "Please read this Risk Disclosure carefully before using Rupora. Digital asset transactions involve risks that you should understand.",
    sections: [
      {
        h: "Market Risk",
        body: "The value of digital assets such as USDT relative to INR can change at any time. Indicative rates may vary based on market conditions, volume and transaction details. The rate confirmed at the time of your transaction applies.",
      },
      {
        h: "Network Risk",
        body: "Blockchain transactions depend on network confirmations. Always send funds on the supported network (TRC20) to the address shown in the app. Funds sent on unsupported networks or to incorrect addresses may be permanently lost.",
      },
      {
        h: "Processing & Banking Risk",
        body: "Payouts depend on banking systems and their operating hours. Delays can occur due to network congestion, bank processing, or additional verification requirements.",
      },
      {
        h: "No Investment Advice",
        body: "Rupora is an exchange service, not an investment product. Nothing on this page or in the app constitutes financial, investment or legal advice. Past rates do not guarantee future rates.",
      },
      {
        h: "Verification & Compliance",
        body: "Transactions are subject to verification and compliance checks. Transactions that fail verification, or that are fraudulent, unauthorized or disputed, may be declined, held or reversed, and are excluded from any guarantee coverage.",
      },
      {
        h: "Your Responsibility",
        body: "Only transact amounts you can afford to have in transit, double-check all details before confirming, and keep your account credentials secure.",
      },
    ],
  },
};
